'use client';
import React from 'react';
import { SourcesPanel } from '@/components/marketing';
/**
 * Sources citation section for concept pages.
 *
 * Displays authoritative sources for claims made in the article with numbered
 * references that correspond to inline citations in the content.
 *
 * ## AEO-Optimized Design
 *
 * This component works with CitedText to create semantic links between claims
 * and their sources. Each source has an anchor ID that inline citations link to,
 * creating a clear claim→source association that AI systems can understand.
 *
 * ## Features
 * - Numbered references matching inline citation order
 * - Anchor IDs for deep linking from inline citations
 * - Accessible links with `target="_blank"` and `rel="noopener noreferrer"`
 * - Optional publication year displayed
 *
 * @see {@link CitedText} - Component for inline citations
 * @see {@link CitationProvider} - Context provider for citation tracking
 */

import { useOrderedSources, useCitationContext } from './CitedText';

/**
 * Renders the sources footer section with numbered references.
 *
 * Must be used within a CitationProvider to access citation ordering.
 */
export function SourcesCitation({
  visualVariant = 'legacy',
}: {
  visualVariant?: 'legacy' | 'marketing';
}): React.ReactElement | null {
  const orderedSources = useOrderedSources();
  const { sourceNumbers } = useCitationContext();

  if (orderedSources.length === 0) return null;

  if (visualVariant === 'marketing')
    return (
      <SourcesPanel>
        <ol>
          {orderedSources.map((source) => (
            <li key={source.id} id={`ref-${source.id}`} value={sourceNumbers.get(source.id)}>
              <a href={source.url} target="_blank" rel="noopener noreferrer">
                {source.name}
              </a>
              {source.year && <> ({source.year})</>}
            </li>
          ))}
        </ol>
      </SourcesPanel>
    );

  return (
    <section id="sources" className="mt-12 border-t border-slate-200 pt-8 scroll-mt-32">
      <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Sources</h2>
      <ol className="mt-4 space-y-3 list-none">
        {orderedSources.map((source) => {
          const number = sourceNumbers.get(source.id);
          return (
            <li
              key={source.id}
              id={`ref-${source.id}`}
              className="flex items-start gap-3 text-sm text-slate-600 scroll-mt-32"
            >
              {/* Citation number badge */}
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                {number}
              </span>
              <span>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#B45309] transition-colors underline decoration-slate-300 underline-offset-2 hover:decoration-[#B45309]"
                >
                  {source.name}
                </a>
                {source.year && <span className="text-slate-400 ml-1">({source.year})</span>}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
